import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, Observable, switchMap, take, throwError } from 'rxjs';
import { selectAuthState } from '../store/auth/selectors/auth.selectors';
import { AuthService } from './auth.service';
import * as AuthActions from '../store/auth/actions/auth.actions';
import * as ErrorActions from '../store/error/actions/error.actions';
import * as NotificationActions from '../store/notification/actions/notification.actions';
import {
  Notification,
  NotificationSeverity,
} from '../store/notification/actions/notification.actions';
import { TokenModel } from '../model/token.model';
import { environment } from '../environments/environment';
import { LoggerService } from './logger.service';

/**
 * Authentication Interceptor
 *
 * Adds Bearer token to all authenticated requests and handles 401 errors
 * by attempting to refresh the token once.
 *
 * Flow:
 * 1. Add Authorization header with access token if user is authenticated
 * 2. If request fails with 401:
 *    - Try to refresh token using refresh token
 *    - Retry original request with new token
 *    - If refresh fails, logout user
 */

export function authIntercept(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const store = inject(Store);
  const authService = inject(AuthService);
  const log = inject(LoggerService);

  // Skip adding auth header for Azure Storage URLs (SAS token already in URL)
  if (req.url.includes('.blob.core.windows.net')) {
    log.debug(
      `[Auth Interceptor] ${req.method} ${req.url} - Azure Storage URL, skipping auth header`,
    );
    return next(req);
  }

  // Skip intercepting /auth/refresh to prevent infinite loops during token refresh
  // Refresh endpoint handles its own auth logic and errors
  if (req.url.includes('/auth/refresh')) {
    log.debug(
      `[Auth Interceptor] ${req.method} ${req.url} - Refresh endpoint, skipping interceptor`,
    );
    return next(req);
  }

  return store.select(selectAuthState).pipe(
    take(1),
    exhaustMap((authState) => {
      // Check if Authorization header already exists (added by token-refresh interceptor)
      const existingAuth = req.headers.get('Authorization');
      if (existingAuth) {
        console.log(
          `[Auth Interceptor] ${req.method} ${req.url} - Token already present, skipping`,
        );
        return next(req);
      }

      // No user authenticated, proceed without token
      if (!authState.isAuthenticated || !authState.token) {
        console.log(`[Auth Interceptor] ${req.method} ${req.url} - NO TOKEN (not authenticated)`);
        return next(req);
      }

      // Add Authorization header with access token
      const modifiedRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${authState.token.accessToken || ''}`,
        },
      });

      // SECURITY: never log token material (no access token, no Authorization header value).
      log.debug(
        `[Auth Interceptor] ${req.method} ${req.url} - Authorization header attached (authenticated=${authState.isAuthenticated})`,
      );

      // Execute request and handle 401 errors
      return next(modifiedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          // Special handling for logout 401 - don't try to refresh, use forceLogout
          if (error.status === 401 && req.url.includes('/auth/logout')) {
            console.error('Logout returned 401 - database may be lost, forcing logout');
            store.dispatch(
              AuthActions.forceLogout({
                reason: 'Logout API returned 401 - server session invalid',
              }),
            );
            return throwError(() => error);
          }

          // Handle 401 Unauthorized errors (not logout)
          if (error.status === 401 && authState.isAuthenticated) {
            console.log('401 error, attempting token refresh...');

            // Try to refresh the token (refresh token sent via HttpOnly cookie)
            return authService.refreshAccessToken().pipe(
              switchMap((refreshResponse) => {
                console.log('Token refreshed after 401, retrying request');

                // Create updated token model
                const newToken = new TokenModel();
                newToken.accessToken = refreshResponse.accessToken;
                newToken.tokenType = 'Bearer';
                newToken.expiresIn = refreshResponse.expiresIn;
                newToken.expiresAt = Date.now() + refreshResponse.expiresIn * 1000;

                // Update token in store
                store.dispatch(AuthActions.tokenRefreshed({ token: newToken }));

                // Retry original request with new token
                const retryRequest = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken.accessToken}`,
                  },
                });

                return next(retryRequest);
              }),
              catchError((refreshError) => {
                console.error('Token refresh failed after 401', refreshError);

                // Check if refresh returned 401 - indicates database loss or invalid refresh token
                // In this case, use forceLogout to avoid API call loops
                if (refreshError instanceof HttpErrorResponse && refreshError.status === 401) {
                  console.error(
                    'Refresh token invalid (401) - database may be lost, forcing logout',
                  );
                  store.dispatch(
                    AuthActions.forceLogout({
                      reason: 'Token refresh failed with 401 - server session no longer valid',
                    }),
                  );
                } else {
                  // For other refresh errors, show notification and logout normally
                  const notification: Notification = {
                    id: Date.now().toString(),
                    severity: 'warning',
                    title: 'Session Expired',
                    summary: 'Your session has expired.',
                    detail:
                      'For security reasons, you are being logged out. Please log in again to continue.',
                    timestamp: new Date(),
                    duration: 0, // Don't auto-dismiss - user needs to see this
                  };
                  store.dispatch(NotificationActions.ShowNotification({ notification }));

                  // Dispatch error to store (this will show error modal for critical errors)
                  if (refreshError instanceof HttpErrorResponse) {
                    store.dispatch(
                      ErrorActions.HttpError({
                        httpError: refreshError,
                        actionType: 'Token Refresh After 401',
                      }),
                    );
                  }

                  // Refresh failed - logout user
                  store.dispatch(AuthActions.logout());
                }

                return throwError(() => refreshError);
              }),
            );
          }

          // Route error to appropriate handler (modal for critical, toast for non-critical)
          if (error instanceof HttpErrorResponse) {
            handleHttpError(error, req, store);
          }
          return throwError(() => error);
        }),
      );
    }),
  );
}

/**
 * Stable error code the backend emits via GlobalExceptionHandler when an
 * OpenAI call fails with insufficient_quota. Surfaced as HTTP 402 so it's
 * unambiguous on the wire — see backend AIErrorAdvice. Keep this string in
 * sync with the Spring constant or the toast won't fire.
 */
const AI_QUOTA_EXCEEDED_CODE = 'AI_QUOTA_EXCEEDED';

/**
 * Routes HTTP errors to either Error Modal (critical) or Notification Toast (non-critical)
 *
 * Critical errors (Error Modal): 500, 502, 503, 504, 401, 403, network errors (0)
 * Non-critical errors (Notification Toast): 400, 404, 409, 422, 429, 408
 *
 * AI-quota override: HTTP 402 with the AI_QUOTA_EXCEEDED envelope is dispatched
 * as a fixed-wording toast so users get a specific call-to-action instead of
 * the generic "Error 402" copy. Early-returns to avoid a second generic toast.
 */
function handleHttpError(error: HttpErrorResponse, req: HttpRequest<unknown>, store: Store): void {
  const actionType = `${req.method} ${req.url.replace(environment.endPoint, '')}`;

  // AI-quota exhaustion gets a dedicated toast — distinct from generic 4xx so
  // the user knows the system isn't broken, the org just needs to top up.
  if (error.status === 402 && error.error?.error === AI_QUOTA_EXCEEDED_CODE) {
    const notification: Notification = {
      id: `ai-quota-${Date.now()}`,
      severity: 'error',
      title: 'AI Unavailable',
      summary: 'AI features are temporarily unavailable. Ask your administrator to add credit.',
      actionType,
      timestamp: new Date(),
      duration: 8000,
      dismissible: true,
    };
    store.dispatch(NotificationActions.ShowNotification({ notification }));
    return;
  }

  const isCriticalError = isCritical(error.status);

  if (isCriticalError) {
    // Critical errors → Error Modal (blocking)
    store.dispatch(
      ErrorActions.HttpError({
        httpError: error,
        actionType,
      }),
    );
  } else {
    // Non-critical errors → Notification Toast (non-blocking)
    const notification: Notification = {
      id: Date.now().toString(),
      severity: getSeverity(error.status),
      title: getErrorTitle(error.status),
      summary: error.error?.message || getDefaultMessage(error.status),
      detail: `Status: ${error.status}`,
      actionType,
      timestamp: new Date(),
      duration: 8000, // 8 seconds for errors
      dismissible: true,
    };
    store.dispatch(NotificationActions.ShowNotification({ notification }));
  }
}

/**
 * Determines if an error is critical (requires modal) or non-critical (notification toast)
 */
function isCritical(statusCode: number): boolean {
  // Critical: Server errors, auth errors, network errors
  return [0, 401, 403, 500, 502, 503, 504].includes(statusCode);
}

/**
 * Maps HTTP status code to notification severity
 */
function getSeverity(statusCode: number): NotificationSeverity {
  if (statusCode >= 500) return 'error';
  if ([400, 422].includes(statusCode)) return 'error';
  if ([404, 409, 429, 408].includes(statusCode)) return 'warning';
  return 'error';
}

/**
 * Gets user-friendly error title based on status code
 */
function getErrorTitle(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return 'Bad Request';
    case 404:
      return 'Not Found';
    case 408:
      return 'Request Timeout';
    case 409:
      return 'Conflict';
    case 422:
      return 'Invalid Data';
    case 429:
      return 'Rate Limited';
    default:
      return `Error ${statusCode}`;
  }
}

/**
 * Gets default error message based on status code
 */
function getDefaultMessage(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return 'The request was invalid. Please check your input.';
    case 404:
      return 'The requested resource was not found.';
    case 408:
      return 'The request took too long. Please try again.';
    case 409:
      return 'The request conflicts with the current state.';
    case 422:
      return 'The data provided is invalid.';
    case 429:
      return 'Too many requests. Please wait before trying again.';
    default:
      return 'An error occurred. Please try again.';
  }
}
